import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminDashboard, adminGetSalesDashboard } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Users, Trophy, Ticket, Coins, TrendingUp, Package, ArrowUpRight, Sparkles, BarChart3, Mail, Tag, RotateCw, Crown, DollarSign, Clock, AlertCircle, Users as UsersIcon, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

export default function AdminDashboard() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeRaffles, setActiveRaffles] = useState([]);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    async function load() {
      try {
        const [statsRes, salesRes] = await Promise.all([
          getAdminDashboard(),
          adminGetSalesDashboard().catch(() => null),
        ]);
        setStats(statsRes.data.stats);
        setActiveRaffles(statsRes.data.active_raffles || []);
        if (salesRes?.data) setSales(salesRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, [user, isAdmin, navigate, authLoading]);

  if (authLoading || !user || !isAdmin) return null;

  const statCards = stats ? [
    { label: t('totalUsers'), value: stats.total_users, icon: Users, accent: 'from-[#0c5e44] to-[#08412f]' },
    { label: t('activeRaffles'), value: stats.active_raffles, icon: Trophy, accent: 'from-[#a07828] to-[#8b6620]', highlight: true },
    { label: t('totalRaffles'), value: stats.total_raffles, icon: Package, accent: 'from-[#0c5e44] to-[#08412f]' },
    { label: t('totalEntries'), value: stats.total_entries, icon: Ticket, accent: 'from-[#0c5e44] to-[#08412f]' },
    { label: t('pointsInCirculation'), value: stats.total_points_circulation, icon: Coins, accent: 'from-[#a07828] to-[#8b6620]', highlight: true },
    { label: t('revenuePoints'), value: stats.total_revenue_points, icon: TrendingUp, accent: 'from-[#0c5e44] to-[#08412f]' },
  ] : [];

  const quickLinks = [
    { label: t('salesDashboard') || 'Ventes', icon: TrendingUp, path: '/admin/sales', accent: true },
    { label: t('raffles'), icon: Trophy, path: '/admin/raffles' },
    { label: t('users'), icon: Users, path: '/admin/users' },
    { label: t('analytics'), icon: BarChart3, path: '/admin/analytics' },
    { label: t('promoCodes'), icon: Tag, path: '/admin/promos' },
    { label: t('newsletter'), icon: Mail, path: '/admin/newsletter' },
    { label: t('vipPlans'), icon: Crown, path: '/admin/vip' },
    { label: t('spinWheel'), icon: RotateCw, path: '/admin/spin' },
  ];

  return (
    <div className="flex min-h-screen bg-rolex-green" data-testid="admin-dashboard" style={{ color: 'hsl(43 38% 92%)' }}>
      <AdminSidebar />
      <main className="flex-1 px-6 lg:px-10 py-10">
        {/* Hero */}
        <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-10">
          <motion.p variants={fadeUp} className="gold-line mb-3"><Sparkles className="w-3 h-3" /> Control Center</motion.p>
          <motion.h1 variants={fadeUp} className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight text-cream">
            {t('dashboard')}
          </motion.h1>
          <motion.p variants={fadeUp} className="text-cream/60 mt-2 text-sm">
            {t('welcomeAdmin')} — <span className="text-gold font-semibold">{user.first_name || user.email}</span>
          </motion.p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats grid */}
            <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
              {statCards.map((stat, i) => (
                <motion.div key={i} variants={fadeUp}>
                  <div className={`relative overflow-hidden rounded-2xl border ${stat.highlight ? 'border-[hsl(var(--gold)/0.5)] gold-glow' : 'border-[hsl(var(--gold)/0.18)]'} bg-gradient-to-br ${stat.accent} p-6 hover:border-[hsl(var(--gold)/0.7)] transition-all duration-300 group`}>
                    <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none" />
                    <div className="relative flex items-start justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-cream/60 mb-2">{stat.label}</p>
                        <p className="font-mono text-4xl font-bold text-cream tabular-nums">
                          {(stat.value || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.highlight ? 'bg-cream/10 border border-[hsl(var(--gold)/0.4)]' : 'bg-cream/5 border border-cream/10'} group-hover:scale-110 transition-transform`}>
                        <stat.icon className="w-6 h-6 text-gold" strokeWidth={1.8} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* ── Sales Quick Widget (priority — most-used by admin) ── */}
            {sales && (
              <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-10">
                <motion.div variants={fadeUp} className="mb-5 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="gold-line mb-2"><Sparkles className="w-3 h-3" /> Aperçu Ventes</p>
                    <h2 className="font-display text-2xl font-bold text-cream">Vue rapide — Ventes & Revenus</h2>
                  </div>
                  <Link to="/admin/sales">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gold-gradient text-rolex-green font-bold text-sm hover:opacity-90 transition-opacity shadow-lg shadow-gold/30">
                      <TrendingUp className="w-4 h-4" /> Détails complets <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </Link>
                </motion.div>

                {/* Stuck payments alert */}
                {sales.summary?.pending?.count > 0 && (
                  <motion.div variants={fadeUp}>
                    <Link to="/admin/sales">
                      <div className="mb-4 p-4 rounded-xl bg-amber-500/15 border-2 border-amber-400/60 flex items-center gap-3 hover:bg-amber-500/20 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                          <AlertCircle className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-amber-200 text-sm">
                            {sales.summary.pending.count} paiement(s) en attente — £{sales.summary.pending.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-amber-100/85">Cliquez pour les récupérer (webhook perdu ?)</p>
                        </div>
                        <ArrowUpRight className="w-5 h-5 text-amber-200" />
                      </div>
                    </Link>
                  </motion.div>
                )}

                {/* 4 mini cards */}
                <motion.div variants={stagger} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    {
                      label: "Aujourd'hui",
                      value: `£${(sales.summary?.today?.revenue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
                      sub: `${sales.summary?.today?.transactions || 0} ventes`,
                      delta: sales.summary?.today?.delta_revenue_pct,
                      icon: DollarSign,
                      tone: 'emerald',
                    },
                    {
                      label: 'Cette semaine',
                      value: `£${(sales.summary?.week?.revenue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
                      sub: `${sales.summary?.week?.transactions || 0} ventes · ${sales.summary?.week?.new_users || 0} nouveaux`,
                      delta: sales.summary?.week?.delta_revenue_pct,
                      icon: TrendingUp,
                      tone: 'gold',
                    },
                    {
                      label: 'Total cumulé',
                      value: `£${(sales.summary?.all_time?.revenue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
                      sub: `${sales.summary?.all_time?.transactions || 0} ventes`,
                      icon: Trophy,
                      tone: 'gold',
                    },
                    {
                      label: 'En attente',
                      value: sales.summary?.pending?.count || 0,
                      sub: `£${(sales.summary?.pending?.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
                      icon: Clock,
                      tone: (sales.summary?.pending?.count || 0) > 0 ? 'amber' : 'neutral',
                    },
                  ].map((kpi, i) => {
                    const tones = {
                      emerald: 'border-emerald-400/50 from-emerald-900/40 to-emerald-950/60',
                      gold:    'border-[hsl(var(--gold)/0.5)] from-[hsl(var(--gold)/0.18)] to-[hsl(var(--gold)/0.05)]',
                      amber:   'border-amber-400/60 from-amber-900/40 to-amber-950/60',
                      neutral: 'border-cream/20 from-rolex-green-elev/50 to-rolex-green/30',
                    };
                    const Icon = kpi.icon;
                    return (
                      <motion.div key={i} variants={fadeUp}>
                        <Link to="/admin/sales">
                          <div className={`relative overflow-hidden rounded-2xl border-2 bg-gradient-to-br ${tones[kpi.tone]} p-4 hover:scale-[1.02] transition-transform h-full`}>
                            <div className="flex items-start justify-between mb-2">
                              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-cream/75">{kpi.label}</p>
                              <Icon className="w-4 h-4 text-gold" />
                            </div>
                            <p className="font-mono text-2xl lg:text-3xl font-extrabold text-cream tabular-nums">{kpi.value}</p>
                            <p className="text-[11px] text-cream/65 mt-1">{kpi.sub}</p>
                            {typeof kpi.delta === 'number' && (
                              <p className={`text-[10px] mt-1.5 font-semibold ${kpi.delta >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                                {kpi.delta >= 0 ? '+' : ''}{kpi.delta}% vs période précédente
                              </p>
                            )}
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </motion.div>
            )}

            {/* ── Active raffles overview (most-actionable view) ── */}
            {activeRaffles.length > 0 && (
              <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-10">
                <motion.div variants={fadeUp} className="mb-5 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="gold-line mb-2"><Trophy className="w-3 h-3" /> Tirages en cours</p>
                    <h2 className="font-display text-2xl font-bold text-cream">
                      {activeRaffles.length} tirage{activeRaffles.length > 1 ? 's' : ''} actif{activeRaffles.length > 1 ? 's' : ''}
                    </h2>
                  </div>
                  <Link to="/admin/raffles">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-cream/10 border border-[hsl(var(--gold)/0.40)] text-gold font-bold text-sm hover:bg-cream/15 transition-colors">
                      <Trophy className="w-4 h-4" /> Gérer les tirages <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </Link>
                </motion.div>

                <motion.div variants={stagger} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {activeRaffles.slice(0, 6).map((r) => {
                    const pct = r.pct_filled || 0;
                    // SOLD OUT = either admin force-flagged it OR all tickets are gone.
                    // This takes priority over "near sold out".
                    const isSoldOut = r.force_sold_out === true || pct >= 100 || r.remaining_tickets === 0;
                    const isNearSellOut = !isSoldOut && pct >= 85;
                    const isHot = !isSoldOut && pct >= 50;
                    const hoursLeft = r.seconds_remaining ? Math.floor(r.seconds_remaining / 3600) : null;
                    const daysLeft = hoursLeft != null ? Math.floor(hoursLeft / 24) : null;
                    const timeLeftLabel = daysLeft != null && daysLeft > 0
                      ? `${daysLeft} j`
                      : (hoursLeft != null && hoursLeft > 0 ? `${hoursLeft} h` : null);
                    return (
                      <motion.div key={r._id} variants={fadeUp}>
                        <Link to={`/admin/raffles?focus=${r._id}`}>
                          <div className={`group relative rounded-2xl overflow-hidden border-2 transition-all hover:scale-[1.01] ${
                            isSoldOut
                              ? 'border-red-500/70 shadow-lg shadow-red-500/15'
                              : isNearSellOut
                              ? 'border-amber-400/70 shadow-lg shadow-amber-500/15'
                              : isHot
                              ? 'border-[hsl(var(--gold)/0.45)]'
                              : 'border-[hsl(var(--gold)/0.18)]'
                          } bg-[hsl(var(--rolex-green-elev)/0.5)] backdrop-blur-sm`}>
                            <div className="flex gap-3 p-4">
                              {r.image_url ? (
                                <img src={r.image_url} alt={r.title} className="w-20 h-20 object-cover rounded-xl border border-[hsl(var(--gold)/0.30)] shrink-0" />
                              ) : (
                                <div className="w-20 h-20 rounded-xl bg-cream/5 border border-[hsl(var(--gold)/0.20)] flex items-center justify-center shrink-0">
                                  <Trophy className="w-8 h-8 text-gold opacity-40" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <p className="font-bold text-cream text-sm leading-tight truncate flex-1">{r.title || '(sans titre)'}</p>
                                  {r.featured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gold/15 border border-gold/30 text-gold font-bold uppercase tracking-wider shrink-0">★</span>}
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-cream/65 flex-wrap mb-2">
                                  <span><span className="text-gold font-mono">£{r.ticket_cost}</span> /ticket</span>
                                  {r.product_value > 0 && (
                                    <>
                                      <span>·</span>
                                      <span>Prix : <span className="text-cream/85 font-mono">£{r.product_value.toLocaleString()}</span></span>
                                    </>
                                  )}
                                </div>
                                {/* Progress bar */}
                                <div className="space-y-1">
                                  <div className="flex justify-between text-[10px] font-mono">
                                    <span className="text-cream/75">
                                      <span className="font-bold text-cream">{r.sold_tickets.toLocaleString()}</span>
                                      <span className="text-cream/55"> / {r.max_tickets.toLocaleString()}</span>
                                    </span>
                                    <span className={`font-bold ${isSoldOut ? 'text-red-400' : isNearSellOut ? 'text-amber-300' : isHot ? 'text-gold' : 'text-cream/75'}`}>{isSoldOut ? '100%' : pct + '%'}</span>
                                  </div>
                                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden border border-cream/10">
                                    <div
                                      className={`h-full transition-all duration-500 ${
                                        isSoldOut ? 'bg-gradient-to-r from-red-500 to-red-600' :
                                        isNearSellOut ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                                        isHot ? 'bg-gradient-to-r from-[#D4AF37] to-[#A07828]' :
                                        'bg-gradient-to-r from-[#0c5e44] to-[#0E4936]'
                                      }`}
                                      style={{ width: `${isSoldOut ? 100 : Math.min(100, pct)}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Footer stats */}
                            <div className="px-4 py-2.5 border-t border-cream/10 flex items-center justify-between gap-2 text-[10px] flex-wrap bg-cream/[0.03]">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1 text-cream/75">
                                  <Ticket className="w-3 h-3 text-emerald-400" />
                                  <span><span className="font-bold text-cream">{r.remaining_tickets.toLocaleString()}</span> restants</span>
                                </div>
                                <div className="flex items-center gap-1 text-cream/75">
                                  <UsersIcon className="w-3 h-3 text-blue-400" />
                                  <span><span className="font-bold text-cream">{r.unique_participants}</span> client{r.unique_participants !== 1 ? 's' : ''}</span>
                                </div>
                                {timeLeftLabel && (
                                  <div className="flex items-center gap-1 text-cream/75">
                                    <Clock className="w-3 h-3 text-amber-400" />
                                    <span>fin : <span className="font-bold text-cream">{timeLeftLabel}</span></span>
                                  </div>
                                )}
                              </div>
                              <div className="text-emerald-300 font-mono font-bold">
                                £{r.revenue_estimate.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                              </div>
                            </div>

                            {/* Hot badges */}
                            {isSoldOut && (
                              <div className="absolute top-2 right-2 px-2.5 py-1 rounded-md bg-red-600 text-white text-[9px] font-extrabold uppercase tracking-wider shadow-lg" data-testid={`raffle-badge-soldout-${r._id}`}>
                                🎯 Sold out
                              </div>
                            )}
                            {!isSoldOut && isNearSellOut && (
                              <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-amber-500/95 text-rolex-green text-[9px] font-extrabold uppercase tracking-wider shadow-lg">
                                🔥 Presque sold out
                              </div>
                            )}
                            {r.coming_soon && !isSoldOut && (
                              <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-blue-500/95 text-cream text-[9px] font-extrabold uppercase tracking-wider">
                                Bientôt
                              </div>
                            )}
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </motion.div>

                {activeRaffles.length > 6 && (
                  <motion.div variants={fadeUp} className="mt-4 text-center">
                    <Link to="/admin/raffles">
                      <button className="text-sm text-cream/65 hover:text-gold inline-flex items-center gap-1 transition-colors">
                        Voir les {activeRaffles.length - 6} autres tirages <ChevronRight className="w-4 h-4" />
                      </button>
                    </Link>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Quick Actions */}
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp} className="mb-5">
                <p className="gold-line mb-2">Shortcuts</p>
                <h2 className="font-display text-2xl font-bold text-cream">{t('quickActions') || 'Quick Actions'}</h2>
              </motion.div>
              <motion.div variants={stagger} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {quickLinks.map((link, i) => (
                  <motion.div key={i} variants={fadeUp}>
                    <Link to={link.path}>
                      <div className={`group rounded-xl border p-4 transition-all duration-200 flex items-center justify-between ${
                        link.accent
                          ? 'border-[hsl(var(--gold)/0.55)] bg-[hsl(var(--gold)/0.10)] hover:bg-[hsl(var(--gold)/0.18)] hover:border-[hsl(var(--gold)/0.80)]'
                          : 'border-[hsl(var(--gold)/0.18)] bg-[hsl(var(--rolex-green-elev)/0.5)] backdrop-blur-sm hover:border-[hsl(var(--gold)/0.55)] hover:bg-[hsl(var(--gold)/0.08)]'
                      }`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center group-hover:bg-[hsl(var(--gold)/0.20)] ${
                            link.accent ? 'bg-[hsl(var(--gold)/0.20)] border border-[hsl(var(--gold)/0.45)]' : 'bg-[hsl(var(--gold)/0.12)] border border-[hsl(var(--gold)/0.25)]'
                          }`}>
                            <link.icon className="w-4 h-4 text-gold" />
                          </div>
                          <span className={`text-sm font-semibold transition-colors ${link.accent ? 'text-gold' : 'text-cream group-hover:text-gold'}`}>{link.label}</span>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-cream/30 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}
